package utils

/**
 * External declarations for DOMPurify HTML sanitization library.
 * DOMPurify must be loaded via script tag or npm package before use.
 *
 * See: https://github.com/cure53/DOMPurify
 */
external interface DOMPurifyAPI {
    /**
     * Sanitizes HTML content to prevent XSS attacks.
     * @param dirty The potentially unsafe HTML string
     * @return A safe HTML string with scripts and dangerous attributes removed
     */
    fun sanitize(dirty: String): String

    /**
     * Sanitize with configuration options.
     * @param dirty The potentially unsafe HTML string
     * @param config Configuration object for custom sanitization behavior
     * @return A safe HTML string
     */
    fun sanitize(dirty: String, config: DOMPurifyConfig): String
}

external interface DOMPurifyConfig {
    val ADD_TAGS: Array<String>?
        get() = definedExternally
    val ADD_ATTR: Array<String>?
        get() = definedExternally
    val FORCE_BODY: Boolean?
        get() = definedExternally
}

/**
 * Global DOMPurify instance. Must be available in the browser environment.
 */
external val DOMPurify: DOMPurifyAPI

/**
 * Sanitizes HTML content using DOMPurify to prevent XSS attacks.
 *
 * This function removes or escapes potentially dangerous content including:
 * - <script> tags and their content
 * - Event handlers like onclick, onerror
 * - javascript: protocol in URLs
 * - Dangerous CSS like expression()
 *
 * @param html The HTML string to sanitize
 * @return A safe HTML string
 */
fun sanitizeHtml(html: String): String {
    return DOMPurify.sanitize(html)
}
